import type { ToolCallMessagePart } from '@assistant-ui/react';
import { z } from 'zod';

import { ERROR_RECOVERY_STRATEGIES } from './errors/base.ts';

/**
 * Successful tool execution result.
 *
 * Carries no human-readable prose — message text is derived on-demand
 * from the tool's `message.result` formatter at render/transport time
 * (passing `(args, dataset?)` for producer tools, `(args, output)` for
 * non-producer tools). Tool-specific output payload (e.g.
 * ask-user-question's `messages` field) is allowed via the
 * discriminated extend.
 */
export const ToolOutputSuccessSchema = z.object({
  success: z.literal(true),
});
/** @public */
export type ToolOutputSuccess = z.infer<typeof ToolOutputSuccessSchema>;

/**
 * Failed tool execution result.
 *
 * Mirrors {@link ToolErrorResponse} (the structured wire shape produced by
 * `ToolExecutionError.toStructuredResponse()`). The full structured
 * payload — errorType, error message, suggestions, details, recovery —
 * is carried across the wire so consumers (`formatErrorText`, the
 * assistant-ui error part, the "Show details" panel) can render it
 * consistently without round-tripping through pre-rendered prose.
 */
export const ToolOutputErrorSchema = z.object({
  success: z.literal(false),
  /** Class name of the thrown error — stable, machine-readable discriminator. */
  errorType: z.string(),
  /** Human-readable error message. */
  error: z.string(),
  /** Actionable recovery hints for the LLM. */
  suggestions: z.array(z.string()).readonly().optional(),
  /** Structured fields the UI / LLM can branch on without parsing the message. */
  details: z.record(z.string(), z.unknown()).optional(),
  /** Recovery strategy hint — what should the LLM do next. */
  recovery: z.enum(ERROR_RECOVERY_STRATEGIES).optional(),
});
/** @public */
export type ToolOutputError = z.infer<typeof ToolOutputErrorSchema>;

/**
 * Tool call part extracted from assistant-ui messages.
 *
 * Structural alias of assistant-ui's {@link ToolCallMessagePart} with two
 * relaxations preserved from the historical local definition:
 *
 * - `args` is widened to `Record<string, unknown>` (canonical default is
 *   `ReadonlyJSONObject`). All accesses in this codebase are reads, and
 *   tests construct partial fixtures, so the wider type is harmless.
 * - `argsText` is optional. The canonical type makes it required, but
 *   internal/test constructions don't always materialize it.
 */
export type ToolCallPart = Omit<ToolCallMessagePart<Record<string, unknown>, unknown>, 'argsText'> & {
  argsText?: string;
};

/**
 * Runtime shape of a tool-call message part. Lenient by design — we don't
 * validate `result`, `argsText`, or status fields, which vary across tools
 * and across the streaming lifecycle.
 */
const ToolCallPartSchema = z.object({
  type: z.literal('tool-call'),
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
});

/**
 * Type guard for tool call parts.
 *
 * Lenient on purpose — `result`, `argsText`, and status fields vary by tool
 * and lifecycle stage, so we only validate the keys every tool call carries.
 */
export function isToolCallPart(value: unknown): value is ToolCallPart {
  return ToolCallPartSchema.safeParse(value).success;
}

/**
 * Flatten an assistant-ui message list to its tool-call parts, in transcript
 * order. The canonical way to read prior tool calls off a thread — callers
 * filter the result by `toolName`. Co-located with {@link isToolCallPart} so
 * every transcript scan shares one extractor instead of re-deriving the
 * message → `content` → tool-call-part walk.
 */
export function toolCallPartsOf(messages: readonly { readonly content?: readonly unknown[] }[]): ToolCallPart[] {
  return messages.flatMap((message) => (message.content ?? []).filter(isToolCallPart));
}

/**
 * Predicate: does a tool-execution output value look like a failure?
 *
 * Recognises the canonical failure envelopes emitted by the `<Tool>`
 * execute boundary:
 *   - {@link ToolOutputErrorSchema}: `{ success: false, errorMessage: string }`
 *   - SDK-level parse failures: `{ error: string }` without a `success` field
 *
 * Used by the chat runtime to flip a tool-call part into the `output-error`
 * state without relying on substring matching against the rendered string,
 * so a tool whose `message` happens to mention the word "error" (e.g. "0
 * errors found") is not misreported as a failure.
 */
export function isToolErrorOutput(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) {
    return false;
  }
  if ('success' in output && output.success === false) {
    return true;
  }
  return 'error' in output && output.error != null;
}
